import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

export interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  role: 'viewer' | 'operator';
}

/**
 * How the org is billed. `stripe` is a self-service signup; `manual` and
 * `internal` are orgs created through this tool, which never touches Stripe.
 */
export type BillingMode = 'stripe' | 'manual' | 'internal';

/** The two an operator may pick — see BillingMode. */
export type AdminBillingMode = Exclude<BillingMode, 'stripe'>;

export interface OrganizationListRow {
  id: string;
  name: string;
  tier: string;
  billingMode: BillingMode;
  createdAt: string;
  userCount: number;
  departmentCount: number;
}

export interface OrganizationDetail extends OrganizationListRow {
  verified: boolean;
  language: string;
  zeloCount: number;
  courseCount: number;
  /**
   * Last Zelo *sent*, not a login. There is no login timestamp anywhere in the
   * schema — see zelo-backend backoffice.dao.ts. Named so the UI cannot imply
   * an activity signal the backend does not have.
   */
  lastZeloSentAt: string | null;
}

export interface OrganizationCourseRow {
  id: string;
  name: string;
  /** Only 'builder' courses are copyable — scorm/steps content lives elsewhere. */
  courseType: string;
  isZeloTemplate: boolean;
  archivedAt: string | null;
  updatedAt: string;
  pageCount: number;
}

export interface OrganizationUserRow {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  source: string | null;
  deletedAt: string | null;
  roles: string[];
}

export interface MergeUser {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  source: string | null;
  status: string | null;
  deletedAt: string | null;
  roles: string[];
  /** Has a WorkOS / Entra / SCIM identity — the customer's directory owns it. */
  directorySynced: boolean;
  departments: { id: string; name: string }[];
  responseCount: number;
}

export interface MergeConflictResponse {
  id: string;
  userId: string;
  dateSent: string | null;
  dateOpened: string | null;
  dateResponded: string | null;
  createdAt: string;
  updatedAt: string;
  round: number;
  stepCount: number;
}

/** Both users have a response for this zelo; the merge keeps a chosen subset. */
export interface MergeResponseConflict {
  zeloId: string;
  zeloTitle: string;
  isTest: boolean;
  responses: MergeConflictResponse[];
  /** What the merge drops unless the operator changes it. */
  defaultDelete: string[];
}

/** Attribute terms are keyed by term; the values are timestamps. */
export interface MergeAttributeConflict {
  attr: string;
  keep: Record<string, unknown>;
  remove: Record<string, unknown>;
}

/** The kept user's email and `source` before and after taking the synced user's. */
export interface MergeEmailChange {
  from: string;
  to: string;
  sourceFrom: string | null;
  sourceTo: string | null;
}

export interface MergePreview {
  keep: MergeUser;
  remove: MergeUser;
  counts: Record<string, number>;
  conflicts: MergeResponseConflict[];
  attributeConflicts: MergeAttributeConflict[];
  attributesAdded: string[];
  departmentsAdded: { id: string; name: string }[];
  /** The kept user takes the directory-synced user's email; see the backend service. */
  emailChange: MergeEmailChange | null;
  blockers: { code: string; message: string }[];
  /** Echoed back on the merge; the backend refuses if the data moved since. */
  fingerprint: string;
}

export interface MergeUsersInput {
  keepUserId: string;
  removeUserId: string;
  fingerprint: string;
  confirmEmail: string;
  deleteResponseIds: string[];
  takeAttributes: string[];
  reason: string;
}

/**
 * What a merge did — the same receipt the backend stores in the audit row.
 * Ids and counts only (the removed email is the one personal detail); the page
 * puts names on it from the preview it just showed.
 */
export interface MergeReceipt {
  keptUserId: string;
  removedUserId: string;
  removedEmail: string;
  emailChange: MergeEmailChange | null;
  counts: Record<string, number>;
  zeloIds: string[];
  deletedResponseIds: string[];
  attributesAdded: string[];
  attributesTaken: string[];
  departmentIdsAdded: string[];
  weeksRecomputed: number;
}

export interface CopyCourseResult {
  course: { id: string; name: string; organizationId: string };
  chapterCount: number;
  pageCount: number;
  copiedAssets: number;
}

export interface OrganizationFlag {
  key: string;
  description: string | null;
  defaultValue: boolean;
  /** null means the org has no row of its own — it is inheriting the default. */
  override: boolean | null;
  /** The org-level value — what admins see. See `departmentIds`. */
  effective: boolean;
  /**
   * Departments the override is narrowed to, or null for the whole org. With a
   * list, admins keep the flag and everyone else needs membership of a listed
   * department or one nested below it.
   */
  departmentIds: string[] | null;
}

/**
 * A row of `GET /organizations/{id}/departments` — every department of the
 * org, inactive ones included (and marked).
 *
 * Departments nest, and a name is unique only among its siblings — "Oslo ›
 * Sales" and "Bergen › Sales" are both "Sales". `label` is the breadcrumb the
 * backend builds from `path` (the bare name for a top-level department), and
 * it is what a flat list shows; a tree renders from `parentId` instead.
 */
export interface OrganizationDepartment {
  id: string;
  name: string;
  parentId: string | null;
  path: string;
  label: string;
  active: boolean;
}

export interface FeatureFlagSummary {
  key: string;
  description: string | null;
  owner: string | null;
  defaultValue: boolean;
  archivedAt: string | null;
  createdAt: string;
  overrideCount: number;
  enabledOrgCount: number;
}

/**
 * One claim of a cron tick: the instance that ran the pass, and — once it
 * finished — how it ended. A row with no `outcome` is a pass still running or
 * one that died with its instance; `staleAfterMinutes` on its job tells the
 * two apart. `outcomeTracked` is false on rows claimed before outcomes were
 * recorded, which never had one to show.
 */
export interface CronRun {
  tickAt: string;
  claimedBy: string;
  claimedAt: string;
  finishedAt: string | null;
  outcome: 'ok' | 'skipped' | 'error' | null;
  statusCode: number | null;
  summary: unknown;
  error: string | null;
  outcomeTracked: boolean;
}

/** An in-process cron job as the server schedules it (all times UTC). */
export interface CronJobSummary {
  job: string;
  label: string;
  schedule: string;
  cadence: string;
  /** The machine route a pass injects — what to call to re-run one by hand. */
  url: string;
  staleAfterMinutes: number;
  /** The latest moment the job was owed a pass. */
  previousRunAt: string;
  nextRunAt: string;
  /** Newest first. */
  runs: CronRun[];
}

/** One of an access key's departments, ordered by `label` server-side. */
export type AccessKeyDepartment = Pick<
  OrganizationDepartment,
  'id' | 'name' | 'label' | 'active'
>;

/**
 * One third-party API key.
 *
 * The secret is never here. Only its sha-256 is stored server-side, so
 * `keyPrefix` (the first 8 characters) is the whole of what the UI can show;
 * the key itself exists in one HTTP response, once, at creation.
 *
 * `unscoped` marks the six keys migrated out of the old hardcoded list in
 * `post.token.js`. They reach **every organization**, exactly as they did when
 * they were literals in the route, until each is bound to its real customer.
 * That is a transitional state, not a feature — nothing can create a new one.
 *
 * An empty `departments` means every department in the key's organization, not
 * none.
 */
export interface AccessKeySummary {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  vendorName: string;
  unscoped: boolean;
  keyPrefix: string;
  note: string | null;
  departments: AccessKeyDepartment[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** The one and only time the plaintext key exists outside the vendor's config. */
export interface MintedAccessKey {
  accessKey: AccessKeySummary;
  key: string;
  warning: string;
}

/**
 * One row of `backoffice_audit` — every staff request, reads included.
 *
 * `payload` is already redacted server-side (`utils/backoffice-audit.ts` strips
 * tokens and passwords before the row is written), so it is safe to render.
 *
 * `actingAsUserId` is null until impersonation ships in phase 4; the column
 * exists now so the rows written before then are still readable afterwards.
 */
export interface AuditEvent {
  id: string;
  staffUserId: string | null;
  staffEmail: string | null;
  staffRole: string | null;
  method: string;
  path: string;
  targetOrganizationId: string | null;
  targetOrganizationName: string | null;
  actingAsUserId: string | null;
  reason: string | null;
  payload: Record<string, unknown> | null;
  statusCode: number | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

/**
 * Keyset, not an offset.
 *
 * Reading the audit log writes audit rows of its own, so the table grows while
 * it is being paged through and an offset walk would show rows it had already
 * shown. The pair is what makes it exact: `created_at` alone cannot separate
 * two rows written in the same instant.
 */
export interface AuditCursor {
  before: string;
  beforeId: string;
}

export interface AuditPage {
  events: AuditEvent[];
  /** Null on the last page — the server over-fetches by one to know that. */
  nextCursor: AuditCursor | null;
}

export const ORGANIZATION_TIERS = [
  'FREE',
  'STARTER',
  'STANDARD',
  'PREMIUM',
  'ENTERPRISE',
  'E2E'
] as const;

export const ORGANIZATION_LANGUAGES = ['en', 'no', 'se', 'dk', 'fi'] as const;

/**
 * Mirrors the server's Joi schema. `tier` and `billingMode` are required with
 * no default on both sides: they are the two fields with consequences outside
 * this tool — tier sets the caps the customer will hit, billing mode decides
 * whether finance counts the org as revenue — and a defaulted field is one
 * nobody chooses.
 */
export interface CreateOrganizationInput {
  name: string;
  tier: (typeof ORGANIZATION_TIERS)[number];
  billingMode: AdminBillingMode;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  language: (typeof ORGANIZATION_LANGUAGES)[number];
  timezone: string;
  flags: string[];
  sendPasswordEmail: boolean;
  reason: string;
}

export interface CreateOrganizationResult {
  organization: {
    id: string;
    name: string;
    tier: string;
    language: string;
    billingMode: BillingMode;
    createdAt: string;
  };
  admin: { id: string; email: string };
  flags: OrganizationFlag[];
  /**
   * Reported rather than thrown. The password goes out through the existing
   * forgot-password flow *after* the org is committed, so a mail failure leaves
   * a correct org that simply needs the email resending — rolling the create
   * back over it would be worse.
   */
  passwordEmailSent: boolean;
  passwordEmailError: string | null;
}

/**
 * The `/v1` is not optional, and it is not part of `apiUrl`.
 *
 * The backend route loader (services/index.js) prefixes every route with its
 * version directory, so a route file declaring "/backoffice/organizations" is
 * served at "/v1/backoffice/organizations". Omitting it 404s every call —
 * including the login exchange, which fails *after* Google has already
 * authenticated, so it surfaces as the confusing "signed in, then nothing".
 */
export const API_BASE = `${environment.apiUrl}/v1/backoffice`;

/**
 * Mutations only.
 *
 * Reads live in the components as `httpResource(...)`, which is reactive: the
 * request re-fires when its signal inputs change and exposes `value()`,
 * `isLoading()` and `error()` directly, so no component hand-rolls loading
 * state. `httpResource` deliberately does not model writes — a write is a
 * one-shot command, not a piece of derived state to keep in sync — so the
 * mutations below stay imperative.
 *
 * `firstValueFrom` is the only RxJS in the app, and only because `HttpClient`
 * has no promise-returning API. Everything downstream of it is signals.
 */
@Injectable({ providedIn: 'root' })
export class BackofficeApiService {
  private readonly http = inject(HttpClient);

  login(idToken: string): Promise<{ token: string; staff: StaffUser }> {
    return firstValueFrom(
      this.http.post<{ token: string; staff: StaffUser }>(
        `${API_BASE}/auth/login`,
        { idToken }
      )
    );
  }

  /**
   * Copies a builder course into another organization — a copy, not a move:
   * the source org keeps its course, zelos and learner data, and the target
   * org gets an independent draft. Operator-only; `reason` is mandatory
   * server-side and lands in the audit log with the payload.
   */
  copyCourse(
    organizationId: string,
    courseId: string,
    targetOrganizationId: string,
    reason: string
  ): Promise<CopyCourseResult> {
    return firstValueFrom(
      this.http.post<CopyCourseResult>(
        `${API_BASE}/organizations/${organizationId}/courses/${courseId}/copy`,
        { targetOrganizationId, reason }
      )
    );
  }

  /**
   * What merging `removeUserId` into `keepUserId` would do. A POST and a
   * one-shot command rather than an `httpResource`: it is computed on demand
   * for a pair the operator has just picked, and its fingerprint belongs to
   * that moment — a reactive refetch would silently replace what was reviewed.
   */
  previewUserMerge(
    organizationId: string,
    keepUserId: string,
    removeUserId: string
  ): Promise<MergePreview> {
    return firstValueFrom(
      this.http.post<MergePreview>(
        `${API_BASE}/organizations/${organizationId}/users/merge/preview`,
        { keepUserId, removeUserId }
      )
    );
  }

  /**
   * Merges a duplicate user into another user and deletes it. Irreversible:
   * the backend checks the preview's fingerprint and the typed email again,
   * and records the outcome in the audit log next to the reason.
   */
  mergeUsers(
    organizationId: string,
    input: MergeUsersInput
  ): Promise<MergeReceipt> {
    return firstValueFrom(
      this.http.post<MergeReceipt>(
        `${API_BASE}/organizations/${organizationId}/users/merge`,
        input
      )
    );
  }

  /**
   * Creates an org and its first admin. No Stripe customer and no subscription
   * — `billingMode` is what records that the absence is deliberate.
   */
  createOrganization(
    input: CreateOrganizationInput
  ): Promise<CreateOrganizationResult> {
    return firstValueFrom(
      this.http.post<CreateOrganizationResult>(
        `${API_BASE}/organizations`,
        input
      )
    );
  }

  /**
   * `value: null` clears the override so the org inherits the flag default —
   * a distinct third state from `false`, which pins it off.
   *
   * `reason` is mandatory server-side; the request is rejected without one.
   */
  setOrganizationFlag(
    organizationId: string,
    key: string,
    value: boolean | null,
    reason: string
  ): Promise<{ flags: OrganizationFlag[] }> {
    return firstValueFrom(
      this.http.put<{ flags: OrganizationFlag[] }>(
        `${API_BASE}/organizations/${organizationId}/flags/${encodeURIComponent(
          key
        )}`,
        { value, reason }
      )
    );
  }

  /**
   * Issues a key for one organization.
   *
   * The response carries the plaintext key and it is never retrievable again —
   * callers must surface it immediately rather than storing it for later.
   *
   * An empty `departmentIds` means the whole organization; listing departments
   * narrows the key to exactly those, which is how one vendor covers several
   * customer areas without a key each.
   */
  createAccessKey(
    organizationId: string,
    body: {
      vendorName: string;
      departmentIds: string[];
      note?: string | null;
      reason: string;
    }
  ): Promise<MintedAccessKey> {
    return firstValueFrom(
      this.http.post<MintedAccessKey>(
        `${API_BASE}/organizations/${organizationId}/access-keys`,
        body
      )
    );
  }

  /**
   * Binds a key to an organization and sets its departments.
   *
   * This is what retires one of the migrated platform-wide keys: the vendor
   * keeps the key already deployed in their system, and it stops reaching the
   * rest of the platform the moment this returns.
   *
   * `departmentIds` replaces rather than merges, so the request always
   * describes the finished state.
   */
  setAccessKeyScope(
    accessKeyId: string,
    body: { organizationId: string; departmentIds: string[]; reason: string }
  ): Promise<{ accessKey: AccessKeySummary }> {
    return firstValueFrom(
      this.http.put<{ accessKey: AccessKeySummary }>(
        `${API_BASE}/access-keys/${accessKeyId}/scope`,
        body
      )
    );
  }

  /** Immediate: tokens already minted from this key stop working too. */
  revokeAccessKey(
    accessKeyId: string,
    reason: string
  ): Promise<{ accessKey: AccessKeySummary }> {
    return firstValueFrom(
      this.http.post<{ accessKey: AccessKeySummary }>(
        `${API_BASE}/access-keys/${accessKeyId}/revoke`,
        { reason }
      )
    );
  }

  /**
   * Narrows an existing override to departments, or (`null`) back to the whole
   * org. The server 409s when the org has no override to narrow — the list
   * only ever restricts a value someone deliberately set.
   */
  setOrganizationFlagDepartments(
    organizationId: string,
    key: string,
    departmentIds: string[] | null,
    reason: string
  ): Promise<{ flags: OrganizationFlag[] }> {
    return firstValueFrom(
      this.http.put<{ flags: OrganizationFlag[] }>(
        `${API_BASE}/organizations/${organizationId}/flags/${encodeURIComponent(
          key
        )}/departments`,
        { departmentIds, reason }
      )
    );
  }
}
